/**
 * Luvè size lineup (sections/luve-size-lineup.liquid): every height of the tree, drawn to scale. It follows the
 * product page's colour and size picker (the trees take the colour, the chosen height stands out), and a height
 * tapped here is picked in that picker, so Horizon updates the price, the delivery dates and the sticky add to cart.
 * Once Horizon has the new variant, an "Add to cart" button appears beside the choice: it presses the product form's
 * own button, as the sticky bar does, so the cart drawer opens as for any other add.
 */
import { StandardEvents } from '@shopify/events';

/** A$ always, no cents on whole dollars: A$1,097, A$19.95 */
const money = (cents) =>
  `A$${(cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * @typedef {{ name: string, tones: string[], sizes: Record<string, { price: number, available: boolean }> }} LineupColour
 * @typedef {{ model?: string, colourOption?: string, sizeOption?: string, colours?: LineupColour[] }} LineupData
 */

/** The page's own product: not a product card's, not quick add's, not the sticky bar's @param {Element} element */
const isMainProduct = (element) => !element.closest('product-card, dialog, sticky-add-to-cart');

class LuveSizeLineup extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {LineupData} */
  #data = {};
  #colour = '';
  #size = '';
  /** The height tapped here, until Horizon has loaded its variant */
  #waiting = '';

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      this.#data = JSON.parse(this.querySelector('[data-lineup-data]')?.textContent || '{}');
    } catch {
      this.#data = {};
    }
    this.#colour = this.dataset.colour || this.#data.colours?.[0]?.name || '';
    this.#size = this.querySelector('[data-lineup-size][aria-pressed="true"]')?.getAttribute('data-lineup-size') || '';

    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { capture: true, signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-lineup-size], [data-lineup-tree], [data-lineup-add]') : null;
    if (!target || !this.contains(target)) return;
    if (target.hasAttribute('data-lineup-add')) {
      this.#addToCart();
      return;
    }
    const value = target.getAttribute('data-lineup-size') ?? target.getAttribute('data-lineup-tree');
    if (value) this.#choose(value);
  };

  /** @param {Event & { selectedOptions?: Array<{ name: string, value: string }>, promise?: Promise<any> }} event */
  #onProductSelect = (event) => {
    const { target } = event;
    if (!(target instanceof Element) || !isMainProduct(target)) return;

    const options = event.selectedOptions || [];
    const colour = options.find((option) => option?.name === this.#data.colourOption)?.value;
    const size = options.find((option) => option?.name === this.#data.sizeOption)?.value;
    if (colour && colour !== this.#colour) this.#paint(colour);
    if (size) this.#mark(size);

    // A height tapped here: offer the add once Horizon has the variant, so the button adds the right tree
    if (this.#waiting && size === this.#waiting) {
      const done = () => {
        if (this.#waiting !== size) return;
        this.#waiting = '';
        this.#offer(size);
      };
      if (event.promise) event.promise.then(done).catch(() => {});
      else done();
    }
  };

  /** Pick a height in the product's own picker (buttons or a dropdown) @param {string} value */
  #choose(value) {
    this.#mark(value);
    const name = this.#data.sizeOption || '';
    const picker = [...document.querySelectorAll('variant-picker')].find(isMainProduct);
    if (!picker) return;

    const input = /** @type {HTMLInputElement | undefined} */ (
      [...picker.querySelectorAll('input[data-option-name]')].find(
        (element) => element instanceof HTMLInputElement && element.dataset.optionName === name && element.value === value
      )
    );
    if (input) {
      if (input.checked) {
        this.#offer(value);
        return;
      }
      this.#waiting = value;
      this.#say(`${value} chosen…`);
      input.click();
      return;
    }

    const option = [...picker.querySelectorAll('option[data-option-name]')].find(
      (element) => element instanceof HTMLOptionElement && element.dataset.optionName === name && element.value === value
    );
    const select = option?.closest('select');
    if (select instanceof HTMLSelectElement) {
      if (select.value === value) {
        this.#offer(value);
        return;
      }
      this.#waiting = value;
      this.#say(`${value} chosen…`);
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** The chosen height stands out, in the drawing and under it @param {string} value */
  #mark(value) {
    this.#size = value;
    for (const button of this.querySelectorAll('[data-lineup-size]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-lineup-size') === value));
    }
    for (const tree of this.querySelectorAll('[data-lineup-tree]')) {
      tree.classList.toggle('is-selected', tree.getAttribute('data-lineup-tree') === value);
    }
  }

  /** The trees and the prices in another colour @param {string} colour */
  #paint(colour) {
    const entry = this.#data.colours?.find((candidate) => candidate.name === colour);
    if (!entry) return;
    this.#colour = colour;
    const [base, highlight, shade, line] = entry.tones;
    this.style.setProperty('--lt-tree-base', base);
    this.style.setProperty('--lt-tree-hi', highlight);
    this.style.setProperty('--lt-tree-shade', shade);
    this.style.setProperty('--lt-tree-line', line);

    const label = this.querySelector('[data-lineup-colour]');
    if (label?.textContent) label.textContent = label.textContent.replace(/ in .*$/, ` in ${colour}`);

    for (const button of this.querySelectorAll('[data-lineup-size]')) {
      const size = entry.sizes[button.getAttribute('data-lineup-size') || ''];
      const price = button.querySelector('[data-lineup-price]');
      if (price) price.textContent = size ? (size.available ? money(size.price) : 'Sold out') : '';
      /** @type {HTMLButtonElement} */ (button).hidden = !size;
    }
    for (const tree of this.querySelectorAll('[data-lineup-tree]')) {
      /** @type {SVGElement} */ (tree).style.display = entry.sizes[tree.getAttribute('data-lineup-tree') || ''] ? '' : 'none';
    }
  }

  /** "240cm in Sage, A$699" and the button that adds it @param {string} value */
  #offer(value) {
    const size = this.#data.colours?.find((entry) => entry.name === this.#colour)?.sizes[value];
    const status = this.querySelector('[data-lineup-status]');
    if (!status) return;
    status.replaceChildren();
    const words = document.createElement('span');
    words.textContent = `${value}${this.#colour ? ` in ${this.#colour}` : ''}${size ? `, ${money(size.price)}` : ''}.`;
    status.append(words);
    if (size?.available && this.#addButton()) {
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'button luve-lineup__add';
      add.dataset.lineupAdd = '';
      add.textContent = 'Add to cart';
      status.append(add);
    }
  }

  /** @param {string} text */
  #say(text) {
    const status = this.querySelector('[data-lineup-status]');
    if (status) status.textContent = text;
  }

  #addButton() {
    return /** @type {HTMLButtonElement | undefined} */ (
      [...document.querySelectorAll('product-form-component [ref="addToCartButton"]')].find(isMainProduct)
    );
  }

  /** The product form's own button, so the add (and the cart drawer) work exactly as at the top of the page */
  #addToCart() {
    const button = this.#addButton();
    if (!button || button.disabled) return;
    button.click();
  }
}

if (!customElements.get('luve-size-lineup')) {
  customElements.define('luve-size-lineup', LuveSizeLineup);
}
